<script setup lang="ts">
    import Highcharts from 'highcharts';
    import HighchartsMore from 'highcharts/highcharts-more';
    import type { Options, Point, Series, SeriesLineOptions } from 'highcharts';
    import { Chart } from 'highcharts-vue';
    import { computed, ref } from 'vue';
    import { IPolarChart } from '@/js/services/api/widget/types';
    import { __ } from '../../../stores/expressions';
    import BaseChart from './BaseChart.vue';

    const initHighchartsMore: ((hc: typeof Highcharts) => void) | null =
        typeof HighchartsMore === 'function'
            ? HighchartsMore
            : typeof (HighchartsMore as Record<string, unknown>)?.default === 'function'
              ? ((HighchartsMore as Record<string, unknown>).default as (
                    hc: typeof Highcharts
                ) => void)
              : null;

    if (initHighchartsMore) {
        initHighchartsMore(Highcharts);
    }

    const props = defineProps<{
        data: IPolarChart['polarChart'];
    }>();
    const rootLevelName: string = __('chart-main-serie');
    const activeCategoryPath = ref<string[]>([]);
    const chartColors: string[] = [
        'var(--color-primary-carrot)',
        'var(--color-primary-beet)',
        'var(--color-primary-lime)',
        'var(--color-primary-acai)',
        'var(--color-primary-blueberry)',
        'var(--color-primary-plum)',
        'var(--color-primary-raspberry)',
        'var(--color-primary-avocado)',
        'var(--color-primary-carrot-400)',
        'var(--color-primary-beet-400)',
        'var(--color-primary-blueberry-400)',
        'var(--color-primary-plum-400)',
        'var(--color-primary-lime-400)',
        'var(--color-primary-acai-600)',
        'var(--color-primary-avocado-400)',
        'var(--color-primary-raspberry-600)',
    ];

    function getTopLevelItems<T extends { parentId: string | null; drillDownDepth: number }>(
        items: T[]
    ): T[] {
        const rootItems = items.filter((item) => !item.parentId);
        if (rootItems.length > 0) {
            return rootItems;
        }

        const minDepth = Math.min(...items.map((item) => item.drillDownDepth));
        return items.filter((item) => item.drillDownDepth === minDepth);
    }

    function getRootSeries<T extends { parentId: string | null; drillDownDepth: number }>(
        items: T[]
    ): T[] {
        if (items.length === 0) {
            return [];
        }

        const minDepth = Math.min(...items.map((item) => item.drillDownDepth));

        return items.filter(
            (item) =>
                item.drillDownDepth === minDepth &&
                (item.parentId === null || item.parentId === undefined || item.parentId === '')
        );
    }

    const categoriesById = computed(
        () => new Map(props.data.categories.map((category) => [category.id, category]))
    );
    const activeCategoryId = computed(() =>
        activeCategoryPath.value.length > 0
            ? activeCategoryPath.value[activeCategoryPath.value.length - 1]
            : null
    );
    const breadcrumbCategories = computed(() =>
        activeCategoryPath.value
            .map((categoryId) => categoriesById.value.get(categoryId))
            .filter((category) => category !== undefined)
    );

    function hasChildCategories(categoryId: string): boolean {
        return props.data.categories.some((category) => category.parentId === categoryId);
    }

    function drillToCategory(categoryId: string | null) {
        console.log('[PolarChart] drillToCategory', { categoryId });
        if (!categoryId) {
            activeCategoryPath.value = [];
            return;
        }

        if (!hasChildCategories(categoryId)) {
            return;
        }

        activeCategoryPath.value = [...activeCategoryPath.value, categoryId];
    }

    function goToBreadcrumb(index: number) {
        activeCategoryPath.value = activeCategoryPath.value.slice(0, index + 1);
    }

    function bindCategoryLabelClicks(chart: Highcharts.Chart) {
        const axis = chart.xAxis[0];
        console.log('[PolarChart] bindCategoryLabelClicks', {
            tickCount: Object.keys(axis?.ticks ?? {}).length,
            activeCategoryId: activeCategoryId.value,
        });

        visibleCategories.value.forEach((category, index) => {
            const tick = axis?.ticks?.[index];
            const label = tick?.label;
            const labelElement = label?.element as
                | ((HTMLElement | SVGElement) & {
                      __polarClickBound?: boolean;
                  })
                | null;
            const isDrillable = hasChildCategories(category.id);

            console.log('[PolarChart] tick candidate', {
                index,
                categoryId: category.id,
                categoryName: category.name,
                isDrillable,
                hasLabel: Boolean(labelElement),
            });

            if (!labelElement || !isDrillable || labelElement.__polarClickBound) {
                return;
            }

            labelElement.__polarClickBound = true;
            if (label?.css) {
                label.css({ cursor: 'pointer', textDecoration: 'underline' });
            }

            Highcharts.addEvent(labelElement, 'click', (event: Event) => {
                event.preventDefault();
                event.stopPropagation();
                console.log('[PolarChart] category label clicked', {
                    index,
                    categoryId: category.id,
                    categoryName: category.name,
                });
                drillToCategory(category.id);
            });
        });
    }

    const unitSuffix = computed(() =>
        props.data.unitType === '%' ? __('percentage-character') : props.data.unitType
    );
    const yAxisMax = computed(() => {
        if (props.data.unitType === '%') {
            return 100;
        }

        const values = props.data.series.flatMap((series) =>
            series.data.flatMap((point) =>
                typeof point.maxPoints === 'number' ? [point.value, point.maxPoints] : [point.value]
            )
        );

        if (values.length === 0) {
            return undefined;
        }

        return Math.max(...values);
    });

    const visibleCategories = computed(() => {
        if (activeCategoryId.value) {
            return props.data.categories.filter(
                (category) => category.parentId === activeCategoryId.value
            );
        }

        return getTopLevelItems(props.data.categories);
    });
    const visibleCategoryIds = computed(() =>
        visibleCategories.value.map((category) => category.id)
    );
    const visibleCategoryNames = computed(() =>
        visibleCategories.value.map((category) => category.name)
    );

    const visibleSeries = computed(() => {
        const seriesAtCurrentLevel = getRootSeries(props.data.series);
        const seriesWithVisibleData = seriesAtCurrentLevel.filter((series) =>
            series.data.some((point) => visibleCategoryIds.value.includes(point.categoryId))
        );

        return seriesWithVisibleData.map((series, index) => {
            const dataByCategoryId = new Map(series.data.map((point) => [point.categoryId, point]));
            const rawSeriesData = visibleCategoryIds.value.map((categoryId) => {
                const point = dataByCategoryId.get(categoryId);
                const y = point
                    ? props.data.unitType === '%'
                        ? parseFloat(point.value.toFixed(1))
                        : point.value
                    : null;

                return {
                    y,
                    custom: {
                        count: point?.count ?? 0,
                        gotPoints: point?.gotPoints ?? null,
                        maxPoints: point?.maxPoints ?? null,
                    },
                };
            });
            const nonNullPointCount = rawSeriesData.filter((point) => point.y !== null).length;
            const seriesData = rawSeriesData.map((point, pointIndex) => {
                if (point.y === null) {
                    return point;
                }

                const previousPoint = rawSeriesData[pointIndex - 1];
                const nextPoint = rawSeriesData[pointIndex + 1];
                const hasAdjacentVisiblePoint = previousPoint?.y !== null || nextPoint?.y !== null;

                return {
                    ...point,
                    marker: {
                        enabled: !hasAdjacentVisiblePoint,
                        symbol: 'diamond',
                        radius: 4,
                    },
                };
            });

            return {
                id: series.id,
                type: 'line',
                name: series.name,
                color: chartColors[index % chartColors.length],
                data: seriesData,
                marker: {
                    enabled: nonNullPointCount <= 1,
                    symbol: 'diamond',
                    radius: 4,
                },
            } satisfies SeriesLineOptions;
        });
    });

    const hasData = computed(
        () =>
            visibleCategories.value.length > 0 &&
            visibleSeries.value.some((series) => series.data.length)
    );
    const chartOptions = computed<Options>(() => ({
        chart: {
            polar: true,
            type: 'line',
            style: {
                fontFamily: 'inherit',
            },
            events: {
                load: function () {
                    bindCategoryLabelClicks(this);
                },
                render: function () {
                    bindCategoryLabelClicks(this);
                },
            },
        },
        title: { text: '' },
        pane: {
            size: '75%',
        },
        xAxis: {
            categories: visibleCategoryNames.value,
            tickmarkPlacement: 'on',
            lineWidth: 0,
            labels: {
                style: {
                    color: '#a5a5a5',
                    fontSize: '12px',
                    fontWeight: '400',
                    textOverflow: 'hidden',
                },
                formatter: function (this: any) {
                    const label = String(this.value ?? '');
                    return label.length > 25 ? `${label.substring(0, 25)}...` : label;
                },
            },
        },
        yAxis: {
            gridLineInterpolation: 'polygon',
            min: 0,
            max: yAxisMax.value,
            lineWidth: 0,
            title: {
                text: undefined,
            },
            labels: {
                style: {
                    fontSize: '12px',
                },
            },
        },
        tooltip: {
            shared: true,
            useHTML: true,
            borderWidth: 0,
            shadow: true,
            backgroundColor: '#fff',
            outside: false,
            distance: 50,
            formatter: function (this: any) {
                const points = this.points ?? [];
                const categoryName =
                    points[0]?.point?.category ?? visibleCategoryNames.value[this.x] ?? this.x;
                const pointRows = points
                    .map((point: { point: Point; series: Series; y: number }) => {
                        const maxPoints = point.point.options.custom?.maxPoints;
                        const gotPoints = point.point.options.custom?.gotPoints;
                        const detail = ` (${gotPoints ?? '?'}/${maxPoints ?? '?'})`;

                        return (
                            `<span style="font-size: 20px; color: ${point.series.color};">◆</span> ` +
                            `${point.series.name} <b>${Math.round(point.y)}</b>${unitSuffix.value}${detail}`
                        );
                    })
                    .join('<br/>');

                return `<strong>${categoryName}</strong><br/>${pointRows}`;
            },
            style: {
                fontFamily: 'inherit',
            },
        },
        legend: {
            enabled: visibleSeries.value.length > 1,
            alignColumns: true,
            layout: 'horizontal',
            maxHeight: 85,
            itemWidth: 190,
            borderColor: '#eee',
            borderWidth: 3,
            itemStyle: {
                color: '#474d58',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
            },
            itemMarginTop: 2,
            itemMarginBottom: 2,
            padding: 12,
            margin: 5,
            squareSymbol: true,
            symbolRadius: 0,
            y: -15,
        },
        exporting: { enabled: false },
        credits: { enabled: false },
        accessibility: {
            enabled: true,
            keyboardNavigation: {
                enabled: true,
            },
        },
        plotOptions: {
            line: {
                cursor: 'pointer',
                marker: {
                    enabled: false,
                    symbol: 'diamond',
                    radius: 3,
                    states: {
                        hover: {
                            lineColor: 'transparent',
                        },
                    },
                },
                states: {
                    hover: {
                        lineWidth: 2,
                    },
                },
            },
            series: {
                pointPlacement: 'on',
                dataLabels: {
                    enabled: false,
                },
                states: {
                    inactive: {
                        enabled: false,
                    },
                },
            },
        },
        colors: chartColors,
        series: visibleSeries.value,
    }));
</script>

<template>
    <div class="absolute inset-x-8 inset-y-0 flex flex-col">
        <div
            class="absolute z-10 mb-4 flex flex-wrap items-center gap-2 text-sm text-gray-700"
            :class="{ invisible: activeCategoryPath.length === 0 }"
        >
            <button
                class="text-left text-gray-700 hover:underline"
                type="button"
                @click="drillToCategory(null)"
            >
                {{ rootLevelName }}
            </button>
            <span>/</span>
            <template v-for="(category, index) in breadcrumbCategories" :key="category.id">
                <button
                    class="text-left text-gray-700 hover:underline"
                    type="button"
                    @click="goToBreadcrumb(index)"
                >
                    {{ category.name }}
                </button>
                <span v-if="index < breadcrumbCategories.length - 1">/</span>
            </template>
        </div>
        <div class="Chart__container">
            <BaseChart :has-data="hasData">
                <Chart
                    v-if="hasData"
                    :options="chartOptions"
                    :update-args="[true, true, { duration: 300 }]"
                    :highcharts="Highcharts"
                />
            </BaseChart>
        </div>
    </div>
</template>
